import React, { useEffect, useState, useMemo } from "react";
import { esSoloLecturaClaro, esContratista, BannerSoloLectura } from "./permisos";

const C = {
  red:     "#E30613",
  redDark: "#B0000E",
  navy:    "#0f172a",
  gray:    "#1e293b",
  slate:   "#64748b",
  light:   "#94a3b8",
  white:   "#ffffff",
};

// ── MAPA: Agrupación → Tipificación RED ──────────────────────────────────────
const AGRUPACION_A_RED = {
  "Afectación multiple":                 "Coaxial",
  "Ausencia energía electrificadora":    "Potencia",
  "Falla corto eléctrico (electrificadora)": "Coaxial",
  "Falla En Fibra Optica":               "Fibra Optica",
  "Falla en Red de Transmisión":         "Trasmision_SDS",
  "Falla en SDS":                        "Trasmision_SDS",
  "Falla energia red propia":            "Coaxial",
  "Falla hurto":                         "Coaxial",
  "Falla sistema de gestión":            "Plataformas",
  "Falla ventana electrificadora":       "Potencia",
  "Falla ventana mejoramiento":          "Coaxial",
  "Falla  endogena implementación":      "Plataformas",
  "Falla endogena implementación":       "Plataformas",
  "Intermitencias":                      "Coaxial",
};

const CAUSA_A_RED = {
  "Falla corte de fibras corto eléctrico":        "Fibra Optica",
  "Falla en la red COAX por corto eléctrico":     "Coaxial",
  "Falla por corto eléctrico red comercial COAX": "Coaxial",
  "Falla después de implementar mejoramiento / proyectos": "Coaxial",
};

const COLOR_RED = {
  "Coaxial":             "#2563eb",
  "Fibra Optica":        "#7c3aed",
  "Potencia":            "#ca8a04",
  "Plataformas":         "#0891b2",
  "Trasmision_SDS":      "#16a34a",
};

function getTipificacionRed(agrupacion, causa) {
  const agr = (agrupacion || "").trim();
  const cau = (causa || "").trim();
  if (CAUSA_A_RED[cau]) return CAUSA_A_RED[cau];
  for (const key of Object.keys(AGRUPACION_A_RED)) {
    if (agr.toLowerCase().includes(key.toLowerCase())) return AGRUPACION_A_RED[key];
  }
  return "Otro";
}

function colorSemaforo(pct) {
  if (pct >= 35) return { bg: "#E30613", texto: "#fff" };
  if (pct >= 25) return { bg: "#ea580c", texto: "#fff" };
  if (pct >= 15) return { bg: "#ca8a04", texto: "#fff" };
  if (pct >= 8)  return { bg: "#16a34a", texto: "#fff" };
  return           { bg: "#15803d", texto: "#fff" };
}

function formatearMes(fecha) {
  if (!fecha) return "";
  if (fecha.includes("T")) {
    const [anio, mes] = fecha.split("T")[0].split("-");
    return `${mes}-${anio}`;
  }
  if (fecha.includes("-") && fecha.length === 10) {
    const [anio, mes] = fecha.split("-");
    return `${mes}-${anio}`;
  }
  return fecha;
}

const filtroLabel = {
  fontSize: "10px", fontWeight: "700", color: "#64748b",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px", display: "block"
};
const filtroSelect = (activo) => ({
  padding: "8px 10px", borderRadius: "7px",
  border: activo ? "1.5px solid #E30613" : "1.5px solid #e2e8f0",
  fontSize: "12px", color: "#0f172a", background: "#f8fafc", cursor: "pointer", width: "100%"
});

// ── COMPONENTE: Gráfica de líneas + tabla alineada abajo ─────────────────────
// La tabla usa posiciones X en % (no px) → nunca desborda la pantalla
let _chartLineasInstance = null;

function GraficaLineasRed({ seriesMensuales, mesesLabel }) {
  // pctCols: array de % de ancho para cada columna de mes, leídos del chart real
  // pctLeft: % que ocupa la zona del eje Y (para la columna de nombres)
  const [pctCols, setPctCols]   = React.useState(null);
  const [pctLeft, setPctLeft]   = React.useState(null);

  useEffect(() => {
    if (!seriesMensuales || seriesMensuales.length === 0) return;
    const totalPorMes = mesesLabel.map((_, mi) =>
      seriesMensuales.reduce((s, sr) => s + (sr.valores[mi] || 0), 0)
    );
    const renderChart = () => {
      const canvas = document.getElementById("graficaLineasRed");
      if (!canvas) return;
      if (_chartLineasInstance) { _chartLineasInstance.destroy(); _chartLineasInstance = null; }
      const datasets = seriesMensuales.map(s => ({
        label: s.red, data: s.valores,
        borderColor: s.color, backgroundColor: "transparent",
        pointBackgroundColor: s.color, pointBorderColor: "#ffffff",
        pointBorderWidth: 2, pointRadius: 6, pointHoverRadius: 9,
        borderWidth: 3, tension: 0.4, fill: false,
      }));
      _chartLineasInstance = new window.Chart(canvas, {
        type: "line",
        data: { labels: mesesLabel, datasets },
        options: {
          responsive: true, maintainAspectRatio: false,
          layout: { padding: { top: 16, right: 0, left: 0, bottom: 0 } },
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "#ffffff", borderColor: "rgba(0,0,0,0.12)", borderWidth: 1,
              titleColor: "#0f172a", bodyColor: "#475569", padding: 12,
              titleFont: { size: 12, weight: "600" }, bodyFont: { size: 11 },
              itemSort: (a, b) => (b.parsed.y || 0) - (a.parsed.y || 0),
              callbacks: {
                title: items => items[0]?.label || "",
                label: ctx => {
                  const val = ctx.parsed.y; if (!val) return null;
                  const pct = Math.round((val / (totalPorMes[ctx.dataIndex] || 1)) * 100);
                  return `  ${ctx.dataset.label}: ${val}  (${pct}%)`;
                },
                afterBody: items => !items.length ? [] :
                  [``, `  Total: ${totalPorMes[items[0].dataIndex]} nodos`],
                labelColor: ctx => ({
                  borderColor: ctx.dataset.borderColor,
                  backgroundColor: ctx.dataset.borderColor,
                  borderWidth: 2, borderRadius: 3,
                }),
              }
            }
          },
          scales: {
            x: { display: false },
            y: { grid: { color: "rgba(0,0,0,0.06)" }, ticks: { color: "#94a3b8", font: { size: 10 } }, border: { display: false }, beginAtZero: true }
          },
          animation: {
            onComplete: () => {
              if (!_chartLineasInstance) return;
              try {
                const chart  = _chartLineasInstance;
                const meta0  = chart.getDatasetMeta(0);
                if (!meta0?.data?.length) return;
                const cw     = chart.canvas.offsetWidth || chart.width || 1;
                const left   = chart.chartArea.left;
                const right  = chart.chartArea.right;
                const dataW  = right - left;
                const xs     = meta0.data.map(pt => pt.x);
                const n      = xs.length;

                // Convertir posiciones X a anchos de columna en % del canvas total
                const cols = xs.map((px, i) => {
                  const lo = i === 0       ? left  : (xs[i-1] + px) / 2;
                  const hi = i === n - 1   ? right : (px + xs[i+1]) / 2;
                  return ((hi - lo) / cw) * 100;
                });

                setPctLeft(((left) / cw) * 100);   // % del canvas que ocupa el eje Y
                setPctCols(cols);
              } catch(e) {}
            },
          },
        }
      });
    };
    if (window.Chart) { renderChart(); }
    else {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
      script.onload = renderChart;
      document.head.appendChild(script);
    }
    return () => { if (_chartLineasInstance) { _chartLineasInstance.destroy(); _chartLineasInstance = null; } };
  }, [seriesMensuales, mesesLabel]);

  if (!seriesMensuales || seriesMensuales.length === 0) return null;

  const totalPorMes = mesesLabel.map((_, mi) =>
    seriesMensuales.reduce((s, sr) => s + (sr.valores[mi] || 0), 0)
  );

  return (
    <div style={{ width: "100%", maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" }}>
      {/* Canvas de líneas */}
      <div style={{ position: "relative", width: "100%", height: "260px", overflow: "hidden" }}>
        <canvas id="graficaLineasRed" style={{ maxWidth: "100%", display: "block" }} />
      </div>

      {/* Tabla alineada con % del canvas */}
      <TablaMeses
        seriesMensuales={seriesMensuales}
        mesesLabel={mesesLabel}
        totalPorMes={totalPorMes}
        pctLeft={pctLeft}
        pctCols={pctCols}
      />

      {/* Leyenda debajo de la tabla — igual que imagen de referencia */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "14px", paddingLeft: "4px" }}>
        {seriesMensuales.map(sr => (
          <span key={sr.red} style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#475569", fontWeight: "600" }}>
            <svg width="28" height="12" style={{ flexShrink: 0 }}>
              <line x1="0" y1="6" x2="20" y2="6" stroke={sr.color} strokeWidth="2.5" />
              <circle cx="24" cy="6" r="4" fill={sr.color} stroke="#fff" strokeWidth="1.5" />
            </svg>
            {sr.red}
          </span>
        ))}
      </div>
    </div>
  );
}

// Tabla alineada con la gráfica usando % en lugar de px — nunca desborda
function TablaMeses({ seriesMensuales, mesesLabel, totalPorMes, pctLeft, pctCols }) {
  const nMeses = mesesLabel.length;
  if (!nMeses) return null;

  // Mientras el chart no termina de animar, columnas iguales
  const cols = pctCols && pctCols.length === nMeses
    ? pctCols
    : Array(nMeses).fill(100 / nMeses);

  // % de ancho de la columna de nombres = exactamente pctLeft del canvas (zona eje Y)
  // Sin mínimos ni máximos — debe coincidir pixel a pixel con el chart
  const namePct = pctLeft != null ? pctLeft : 12;

  // Las cols ya vienen calculadas como % del canvas total (desde chartArea)
  // namePct también es % del canvas total → cols ya están alineadas, no escalar
  const scaledCols = cols;

  const thMes = {
    textAlign: "center", color: "#475569", fontWeight: "700",
    fontSize: "10px", borderBottom: "2px solid #e2e8f0",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    background: "#f8fafc", padding: "5px 1px",
  };
  const tdVal = {
    textAlign: "center", borderBottom: "1px solid #f1f5f9",
    fontSize: "10px", overflow: "hidden", padding: "4px 1px",
  };

  return (
    <div style={{
      borderTop: "2px solid #e2e8f0", marginTop: "4px",
      width: "100%", maxWidth: "100%", boxSizing: "border-box", overflow: "hidden",
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <colgroup>
          {/* Columna de nombres — exactamente el mismo % que chartArea.left / canvasW */}
          <col style={{ width: `${namePct.toFixed(4)}%` }} />
          {/* Columnas de meses — % proporcionales al espacio entre puntos del chart */}
          {scaledCols.map((w, i) => <col key={i} style={{ width: `${w.toFixed(4)}%` }} />)}
          {/* Columna residual para absorber decimales y llegar al 100% exacto */}
          <col style={{ width: `${Math.max(0, 100 - namePct - scaledCols.reduce((a,b)=>a+b,0)).toFixed(4)}%` }} />
        </colgroup>

        {/* Header: vacío + meses */}
        <thead>
          <tr>
            <th style={{ ...thMes, borderRight: "2px solid #e2e8f0", background: "#f8fafc" }} />
            {mesesLabel.map((m, i) => <th key={i} style={thMes}>{m}</th>)}
            <th style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }} />
          </tr>
        </thead>

        {/* Filas por tipificación */}
        <tbody>
          {seriesMensuales.map((sr, si) => (
            <tr key={sr.red} style={{ background: si % 2 === 0 ? "#fff" : "#f8fafc", height: "23px" }}>
              {/* Celda nombre con leyenda */}
              <td style={{ padding: "3px 6px", borderRight: "2px solid #e2e8f0", overflow: "hidden" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", maxWidth: "100%" }}>
                  <svg width="20" height="10" style={{ flexShrink: 0 }}>
                    <line x1="0" y1="5" x2="13" y2="5" stroke={sr.color} strokeWidth="2" />
                    <circle cx="17" cy="5" r="3" fill={sr.color} stroke="#fff" strokeWidth="1.2" />
                  </svg>
                  <span style={{ color: "#0f172a", fontWeight: "600", fontSize: "10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {sr.red}
                  </span>
                </span>
              </td>
              {/* Valores por mes */}
              {sr.valores.map((v, mi) => {
                const pct = totalPorMes[mi] > 0 ? Math.round((v / totalPorMes[mi]) * 100) : 0;
                return (
                  <td key={mi} style={tdVal}>
                    {v > 0
                      ? <><span style={{ color: sr.color, fontWeight: "700" }}>{v}</span><span style={{ color: "#475569", fontSize: "10px", fontWeight: "600" }}> ({pct}%)</span></>
                      : <span style={{ color: "#e2e8f0" }}>—</span>
                    }
                  </td>
                );
              })}
              <td style={{ borderBottom: "1px solid #f1f5f9" }} />
            </tr>
          ))}

          {/* Fila Total */}
          <tr style={{ background: "#0f172a", height: "25px" }}>
            <td style={{ padding: "4px 6px", color: "#fff", fontWeight: "700", fontSize: "10px", borderRight: "2px solid #334155" }}>Total</td>
            {totalPorMes.map((t, mi) => (
              <td key={mi} style={{ padding: "4px 1px", textAlign: "center", color: "#fff", fontWeight: "700", fontSize: "10px" }}>{t}</td>
            ))}
            <td style={{ background: "#0f172a" }} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}


// ── COMPONENTE: Gráfica de barras horizontales SVG ───────────────────────────
function GraficaBarrasRed({ datos }) {
  if (!datos || datos.length === 0) return null;
  const BAR_HEIGHT = 32;
  const GAP = 10;
  const LABEL_W = 140;
  const SVG_W = 560;
  const BAR_W = SVG_W - LABEL_W - 80;
  const SVG_H = datos.length * (BAR_HEIGHT + GAP) + 20;
  const maxVal = Math.max(...datos.map(d => d.nodos), 1);

  return (
    <svg width="100%" viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ overflow: "visible", fontFamily: "inherit" }}>
      {datos.map((d, i) => {
        const y = i * (BAR_HEIGHT + GAP) + 10;
        const barLen = Math.max((d.nodos / maxVal) * BAR_W, 4);
        const color = COLOR_RED[d.red] || "#64748b";
        return (
          <g key={d.red}>
            <text x={LABEL_W - 8} y={y + BAR_HEIGHT / 2 + 5}
              textAnchor="end" fontSize="11" fill="#0f172a" fontWeight="600">{d.red}</text>
            <rect x={LABEL_W} y={y} width={BAR_W} height={BAR_HEIGHT} rx="5" fill="#f1f5f9" />
            <rect x={LABEL_W} y={y} width={barLen} height={BAR_HEIGHT} rx="5" fill={color} opacity="0.88" />
            <text x={LABEL_W + barLen + 8} y={y + BAR_HEIGHT / 2 + 5}
              fontSize="12" fill={color} fontWeight="700">{d.nodos}</text>
            <text x={SVG_W - 2} y={y + BAR_HEIGHT / 2 + 5}
              textAnchor="end" fontSize="11" fill="#64748b" fontWeight="600">{d.pct}%</text>
          </g>
        );
      })}
    </svg>
  );
}

// Normaliza nombres equivalentes de causas de falla
function normalizarCausa(causa) {
  if (!causa) return causa;
  // Unificar variantes con typos o diferencias menores
  if (causa === "Falla por corto eléctrico red comercial COAX")
    return "Falla en la red COAX por corto eléctrico";
  // "no contac rate" (sin t) → "no contact rate"
  if (causa.toLowerCase().includes("no contac rate") && !causa.toLowerCase().includes("no contact rate"))
    return causa.replace(/no contac rate/gi, "no contact rate");
  return causa;
}

function CausasFalla({ usuario }) {
  const soloLectura = esSoloLecturaClaro(usuario) || esContratista(usuario);
  const [data, setData]                  = useState([]);
  const [mesSeleccionado, setMes]        = useState([]);
  const [mesesDisponibles, setMeses]     = useState([]);
  const [filtroJefe, setFiltroJefe]      = useState([]);
  const [filtroAliado, setFiltroAliado]  = useState([]);
  const [filtroCiudad, setFiltroCiudad]  = useState([]);
  const [filtroPrioridad, setFiltroPrio] = useState([]);
  const [filtroCausa, setFiltroCausa]     = useState([]);

  const [expandidas, setExpandidas]       = useState({});
  const [expandidasRed, setExpandidasRed] = useState({});

  const toggleExpand    = (agr) => setExpandidas(prev    => ({ ...prev, [agr]: !prev[agr] }));
  const toggleExpandRed = (red) => setExpandidasRed(prev => ({ ...prev, [red]: !prev[red] }));

  useEffect(() => {
    const rol    = encodeURIComponent(usuario?.rol || "");
    const cedula = encodeURIComponent(usuario?.cedula || "");
    const aliado = encodeURIComponent(usuario?.aliado || "");
    fetch(`${process.env.REACT_APP_API_URL}/causas-falla?rol=${rol}&cedula=${cedula}&aliado=${aliado}`)
      .then(res => res.json())
      .then(rows => {
        setData(rows);
        const meses = [...new Set(rows.map(r => formatearMes(r.m_fecha_mes)))]
          .filter(Boolean)
          .sort((a, b) => {
            const [mA, aA] = a.split("-").map(Number);
            const [mB, aB] = b.split("-").map(Number);
            return new Date(aB, mB - 1) - new Date(aA, mA - 1);
          });
        setMeses(meses);
        if (meses.length > 0) setMes([meses[0]]);
      })
      .catch(err => console.error(err));
  }, [usuario]);

  // ── OPCIONES BIDIRECCIONALES ──────────────────────────────────────────────
  const opcionesAliado = useMemo(() => {
    const base = data.filter(r => {
      const mes  = formatearMes(r.m_fecha_mes);
      const ciu  = (r.ciudad || "").trim();
      const jefe = (r.nombre_jefe || "").trim();
      const prio = (r.tipo_prioridad || "").trim();
      return (
        (mesSeleccionado.length === 0 || mesSeleccionado.includes(mes)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciu)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prio))
      );
    });
    return [...new Set(base.map(r => (r.aliado || "").trim()))].filter(Boolean).sort();
  }, [data, mesSeleccionado, filtroCiudad, filtroJefe, filtroPrioridad]);

  const opcionesCiudad = useMemo(() => {
    const base = data.filter(r => {
      const mes    = formatearMes(r.m_fecha_mes);
      const aliado = (r.aliado || "").trim();
      const jefe   = (r.nombre_jefe || "").trim();
      const prio   = (r.tipo_prioridad || "").trim();
      return (
        (mesSeleccionado.length === 0 || mesSeleccionado.includes(mes)) &&
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prio))
      );
    });
    return [...new Set(base.map(r => (r.ciudad || "").trim()))].filter(Boolean).sort();
  }, [data, mesSeleccionado, filtroAliado, filtroJefe, filtroPrioridad]);

  const opcionesJefe = useMemo(() => {
    const base = data.filter(r => {
      const mes    = formatearMes(r.m_fecha_mes);
      const aliado = (r.aliado || "").trim();
      const ciu    = (r.ciudad || "").trim();
      const prio   = (r.tipo_prioridad || "").trim();
      return (
        (mesSeleccionado.length === 0 || mesSeleccionado.includes(mes)) &&
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciu)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prio))
      );
    });
    return [...new Set(base.map(r => (r.nombre_jefe || "").trim()))].filter(Boolean).sort();
  }, [data, mesSeleccionado, filtroAliado, filtroCiudad, filtroPrioridad]);

  const opcionesPrioridad = useMemo(() => {
    const base = data.filter(r => {
      const mes    = formatearMes(r.m_fecha_mes);
      const aliado = (r.aliado || "").trim();
      const ciu    = (r.ciudad || "").trim();
      const jefe   = (r.nombre_jefe || "").trim();
      return (
        (mesSeleccionado.length === 0 || mesSeleccionado.includes(mes)) &&
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciu)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe))
      );
    });
    return [...new Set(base.map(r => (r.tipo_prioridad || "").trim()))].filter(Boolean).sort();
  }, [data, mesSeleccionado, filtroAliado, filtroCiudad, filtroJefe]);

  // Opciones de Causa de Falla (bidireccional)
  const { opcionesCausa, countSinCausa } = useMemo(() => {
    const base = data.filter(r => {
      const mes       = formatearMes(r.m_fecha_mes);
      const aliado    = (r.aliado         || "").trim();
      const ciudad    = (r.ciudad         || "").trim();
      const jefe      = (r.nombre_jefe    || "").trim();
      const prioridad = (r.tipo_prioridad || "").trim();
      return (
        (mesSeleccionado.length === 0 || mesSeleccionado.includes(mes)) &&
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prioridad))
      );
    });
    // Contar registros sin causa asignada
    const sinCausa = base.filter(r => !r.causa_falla || r.causa_falla.trim() === "").length;
    // Causas con valor, ordenadas
    const causasConValor = [...new Set(
      base.map(r => normalizarCausa((r.causa_falla || "").trim())).filter(Boolean)
    )].sort();
    // "Sin causa asignada" va primero si hay registros sin causa
    const lista = sinCausa > 0
      ? ["⚠ Sin causa asignada", ...causasConValor]
      : causasConValor;
    return { opcionesCausa: lista, countSinCausa: sinCausa };
  }, [data, mesSeleccionado, filtroAliado, filtroCiudad, filtroJefe, filtroPrioridad]);

  const datosFiltrados = useMemo(() => {
    return data.filter(r => {
      const mes       = formatearMes(r.m_fecha_mes);
      const aliado    = (r.aliado         || "").trim();
      const ciudad    = (r.ciudad         || "").trim();
      const jefe      = (r.nombre_jefe    || "").trim();
      const prioridad = (r.tipo_prioridad || "").trim();
      const causa     = normalizarCausa((r.causa_falla || "").trim());
      return (
        (mesSeleccionado.length === 0 || mesSeleccionado.includes(mes)) &&
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prioridad)) &&
        (filtroCausa.length     === 0 || filtroCausa.includes(causa))
      );
    });
  }, [data, mesSeleccionado, filtroAliado, filtroCiudad, filtroJefe, filtroPrioridad, filtroCausa]);

  const total = datosFiltrados.length;

  // ── Tabla 1: Causas Agrupadas ICR ─────────────────────────────────────────
  const tabla1 = useMemo(() => {
    const map = {};
    datosFiltrados.forEach(r => {
      const agr   = (r.agrupacion  || "Sin agrupacion").trim();
      const causa = normalizarCausa((r.causa_falla || "Sin causa").trim());
      if (!map[agr]) map[agr] = {};
      map[agr][causa] = (map[agr][causa] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => {
        const tA = Object.values(a[1]).reduce((s, v) => s + v, 0);
        const tB = Object.values(b[1]).reduce((s, v) => s + v, 0);
        return tB - tA;
      })
      .map(([agrupacion, causasMap]) => {
        const nodos = Object.values(causasMap).reduce((s, v) => s + v, 0);
        const causas = Object.entries(causasMap)
          .sort((a, b) => b[1] - a[1])
          .map(([causa, cnt]) => ({
            causa, nodos: cnt,
            pct: nodos > 0 ? Math.round((cnt / nodos) * 100) : 0,
          }));
        return { agrupacion, nodos, pct: total > 0 ? Math.round((nodos / total) * 100) : 0, causas };
      });
  }, [datosFiltrados, total]);

  // ── Tabla 2: Causas Incumplimiento ICR ───────────────────────────────────
  const tabla2 = useMemo(() => {
    const counts = {};
    datosFiltrados.forEach(r => {
      const causa = (r.causa_incumplimiento_icr || "Sin causa").trim();
      if (causa) counts[causa] = (counts[causa] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([causa, nodos]) => ({ causa, nodos, pct: total > 0 ? Math.round((nodos / total) * 100) : 0 }));
  }, [datosFiltrados, total]);

  // ── Tabla Tipificación RED (expandible) ───────────────────────────────────
  const tablaRed = useMemo(() => {
    const map = {};
    datosFiltrados.forEach(r => {
      const agr = (r.agrupacion || "").trim();
      const cau = normalizarCausa((r.causa_falla || "Sin causa").trim());
      const red = getTipificacionRed(agr, cau);
      if (!map[red]) map[red] = {};
      map[red][cau] = (map[red][cau] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => {
        const tA = Object.values(a[1]).reduce((s, v) => s + v, 0);
        const tB = Object.values(b[1]).reduce((s, v) => s + v, 0);
        return tB - tA;
      })
      .map(([red, causasMap]) => {
        const nodos = Object.values(causasMap).reduce((s, v) => s + v, 0);
        const causas = Object.entries(causasMap)
          .sort((a, b) => b[1] - a[1])
          .map(([causa, cnt]) => ({
            causa, nodos: cnt,
            pct: nodos > 0 ? Math.round((cnt / nodos) * 100) : 0,
          }));
        return { red, nodos, pct: total > 0 ? Math.round((nodos / total) * 100) : 0, causas };
      });
  }, [datosFiltrados, total]);

  // ── Series mensuales para gráfica de líneas ─────────────────────────────
  // Respeta todos los filtros; cuando hay mes seleccionado muestra ese mes y anteriores
  const { seriesMensuales, mesesLabel } = useMemo(() => {
    const base = data.filter(r => {
      const aliado    = (r.aliado         || "").trim();
      const ciudad    = (r.ciudad         || "").trim();
      const jefe      = (r.nombre_jefe    || "").trim();
      const prioridad = (r.tipo_prioridad || "").trim();
      const causa     = normalizarCausa((r.causa_falla || "").trim());
      return (
        (filtroAliado.length    === 0 || filtroAliado.includes(aliado)) &&
        (filtroCiudad.length    === 0 || filtroCiudad.includes(ciudad)) &&
        (filtroJefe.length      === 0 || filtroJefe.includes(jefe)) &&
        (filtroPrioridad.length === 0 || filtroPrioridad.includes(prioridad)) &&
        (filtroCausa.length     === 0 || filtroCausa.includes(causa))
      );
    });

    // Todos los meses disponibles en orden cronológico
    const todosMeses = [...new Set(base.map(r => formatearMes(r.m_fecha_mes)))]
      .filter(Boolean)
      .sort((a, b) => {
        const [mA, aA] = a.split("-").map(Number);
        const [mB, aB] = b.split("-").map(Number);
        return new Date(aA, mA - 1) - new Date(aB, mB - 1);
      });

    // Si hay mes(es) seleccionado(s), tomar el MÁS RECIENTE como límite
    // y mostrar todos los meses desde el inicio HASTA ese mes (inclusive)
    const parseMes = (m) => { const [mm, aa] = m.split("-").map(Number); return new Date(aa, mm - 1); };
    let mesesSet = todosMeses;
    if (mesSeleccionado.length === 1) {
      // 1 mes seleccionado → mostrar ese mes y todos los anteriores
      const fechaFin = parseMes(mesSeleccionado[0]);
      mesesSet = todosMeses.filter(m => parseMes(m) <= fechaFin);
    } else if (mesSeleccionado.length > 1) {
      // 2+ meses seleccionados → mostrar exactamente esos meses en orden cronológico
      mesesSet = todosMeses.filter(m => mesSeleccionado.includes(m));
    }

    const redesSet = new Set();
    base.forEach(r => {
      const agr = (r.agrupacion || "").trim();
      const cau = normalizarCausa((r.causa_falla || "").trim());
      redesSet.add(getTipificacionRed(agr, cau));
    });

    const mapaConteo = {};
    base.forEach(r => {
      const mes = formatearMes(r.m_fecha_mes);
      const agr = (r.agrupacion || "").trim();
      const cau = normalizarCausa((r.causa_falla || "").trim());
      const red = getTipificacionRed(agr, cau);
      if (!mapaConteo[mes]) mapaConteo[mes] = {};
      mapaConteo[mes][red] = (mapaConteo[mes][red] || 0) + 1;
    });

    const series = [...redesSet].map(red => ({
      red,
      color: COLOR_RED[red] || "#64748b",
      valores: mesesSet.map(mes => mapaConteo[mes]?.[red] || 0),
    }));

    series.sort((a, b) =>
      b.valores.reduce((s, v) => s + v, 0) - a.valores.reduce((s, v) => s + v, 0)
    );

    return { seriesMensuales: series, mesesLabel: mesesSet };
  }, [data, mesSeleccionado, filtroAliado, filtroCiudad, filtroJefe, filtroPrioridad, filtroCausa]);

  const hayFiltrosActivos = mesSeleccionado.length > 0 || filtroJefe.length > 0 || filtroAliado.length > 0 || filtroCiudad.length > 0 || filtroPrioridad.length > 0 || filtroCausa.length > 0;

  const limpiarTodo = () => {
    setMes([]); setFiltroJefe([]);
    setFiltroAliado([]); setFiltroCiudad([]); setFiltroPrio([]); setFiltroCausa([]);
  };

  const cardStyle = { background: C.white, borderRadius: "12px", padding: "20px 24px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", boxSizing: "border-box", minWidth: 0, overflow: "hidden" };
  const thStyle   = { padding: "10px 12px", background: C.navy, color: "white", textAlign: "left", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap" };
  const tdStyle   = { padding: "8px 12px", fontSize: "13px", borderBottom: "1px solid #f1f5f9", color: C.navy };

  const badge = (pct) => {
    const col = colorSemaforo(pct);
    return <span style={{ display: "inline-block", background: col.bg, color: col.texto, fontWeight: "700", fontSize: "12px", padding: "3px 10px", borderRadius: "4px", minWidth: "48px", textAlign: "center" }}>{pct}%</span>;
  };

  const MultiFiltro = ({ label, opciones, valor, onChange }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <label style={filtroLabel}>{label}</label>
      <select multiple value={valor}
        onChange={(e) => onChange(Array.from(e.target.selectedOptions).map(o => o.value))}
        size={Math.min(opciones.length, 5)}
        style={{ ...filtroSelect(valor.length > 0), minWidth: 0, width: "100%" }}>
        {opciones.map((op, i) => <option key={i} value={op}>{op}</option>)}
      </select>
      {valor.length > 0 && (
        <button onClick={() => onChange([])} style={{ fontSize: "11px", color: C.red, background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>
          ✕ Limpiar
        </button>
      )}
    </div>
  );

  return (
    <div style={{ padding: "4px 0", boxSizing: "border-box", width: "100%", overflowX: "hidden" }}>

      {/* Título */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <div style={{ width: "4px", height: "24px", background: C.red, borderRadius: "2px" }} />
        <h2 style={{ margin: 0, color: C.navy, fontSize: "20px", fontWeight: "600" }}>Causas de Falla Ofensores</h2>
        {soloLectura && <BannerSoloLectura />}
      </div>

      {/* FILTROS */}
      <div style={{ ...cardStyle, marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
            <span style={{ fontSize: "13px", fontWeight: "700", color: C.navy, textTransform: "uppercase", letterSpacing: "0.05em" }}>Filtros</span>
            <span style={{ fontSize: "11px", color: C.slate }}>— Ctrl para seleccionar varios. Sin selección = todos.</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: "#475569", fontWeight: "600" }}>
              {total} registros
            </div>
            {hayFiltrosActivos && (
              <button onClick={limpiarTodo} style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "20px", padding: "4px 12px", fontSize: "12px", color: C.red, fontWeight: "600", cursor: "pointer" }}>
                ✕ Limpiar filtros
              </button>
            )}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "10px" }}>
          {/* Mes, Aliado, Ciudad, Prioridad — ancho normal */}
          <div><MultiFiltro label="Mes"       opciones={mesesDisponibles} valor={mesSeleccionado} onChange={setMes} /></div>
          <div><MultiFiltro label="Aliado"    opciones={opcionesAliado}   valor={filtroAliado}    onChange={setFiltroAliado} /></div>
          <div><MultiFiltro label="Ciudad"    opciones={opcionesCiudad}   valor={filtroCiudad}    onChange={setFiltroCiudad} /></div>
          <div><MultiFiltro label="Prioridad" opciones={opcionesPrioridad} valor={filtroPrioridad} onChange={setFiltroPrio} /></div>

          {/* Jefe de Zona — doble ancho */}
          <div style={{ gridColumn: "span 1", minWidth: 0 }}>
            <MultiFiltro label="Jefe de Zona" opciones={opcionesJefe} valor={filtroJefe} onChange={setFiltroJefe} />
          </div>

          {/* Causa de Falla — doble ancho con alerta */}
          <div style={{ gridColumn: "span 1", minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
            <label style={filtroLabel}>
              Causa de Falla
              {countSinCausa > 0 && (
                <span style={{ marginLeft: "6px", background: "#fef9c3", color: "#92400e", border: "1px solid #fde68a", borderRadius: "4px", padding: "1px 6px", fontSize: "10px", fontWeight: "700" }}>
                  ⚠ {countSinCausa} sin asignar
                </span>
              )}
            </label>
            <select multiple value={filtroCausa}
              onChange={(e) => setFiltroCausa(Array.from(e.target.selectedOptions).map(o => o.value))}
              size={Math.min(opcionesCausa.length, 5)}
              style={{ ...filtroSelect(filtroCausa.length > 0), width: "100%", minWidth: 0 }}>
              {opcionesCausa.map((op, i) => (
                <option key={i} value={op}
                  style={op.startsWith("⚠") ? { background: "#fef9c3", color: "#92400e", fontWeight: "700" } : {}}>
                  {op}
                </option>
              ))}
            </select>
            {filtroCausa.length > 0 && (
              <button onClick={() => setFiltroCausa([])} style={{ fontSize: "11px", color: C.red, background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>
                ✕ Limpiar
              </button>
            )}
          </div>
        </div>
        <div style={{ marginTop: "14px" }}>
          <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: "8px", padding: "8px 16px", display: "inline-flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "11px", color: C.slate, fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Ofensores N2+</span>
            <span style={{ fontSize: "24px", fontWeight: "700", color: C.red }}>{total}</span>
          </div>
        </div>
      </div>

      {/* ── 1. GRÁFICA DE LÍNEAS MENSUAL (ancho completo, primera sección) ── */}
      <div style={{ ...cardStyle, marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
          <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
          <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Evolución mensual por Tipificación de Red</h3>
        </div>

        {seriesMensuales.length === 0
          ? <p style={{ color: C.slate, fontSize: "13px" }}>Sin datos</p>
          : <GraficaLineasRed seriesMensuales={seriesMensuales} mesesLabel={mesesLabel} />
        }
      </div>

      {/* ── 2. TABLA TIPIFICACIÓN RED + GRÁFICA DE BARRAS (grilla 2 col) ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px", alignItems: "start", marginBottom: "20px" }}>

        {/* Tabla Tipificación RED expandible */}
        <div style={{ ...cardStyle }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
            <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Resumen por Tipificación de Red</h3>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: "22px", padding: "10px 4px" }}></th>
                <th style={thStyle}>Tipificación RED</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Nodos</th>
                <th style={{ ...thStyle, textAlign: "center" }}>% Part</th>
              </tr>
            </thead>
            <tbody>
              {tablaRed.length === 0
                ? <tr><td colSpan={4} style={{ ...tdStyle, textAlign: "center", color: C.slate }}>Sin datos</td></tr>
                : tablaRed.map((row, i) => {
                    const color   = COLOR_RED[row.red] || "#64748b";
                    const abierta = !!expandidasRed[row.red];
                    return (
                      <>
                        <tr key={`r-${i}`} onClick={() => toggleExpandRed(row.red)}
                          style={{ cursor: "pointer", background: abierta ? "#f0f4ff" : (i % 2 === 0 ? C.white : "#f8fafc"), borderLeft: abierta ? `3px solid ${color}` : "3px solid transparent" }}>
                          <td style={{ ...tdStyle, textAlign: "center", padding: "8px 4px", fontSize: "10px", color: C.slate, userSelect: "none" }}>{abierta ? "▼" : "▶"}</td>
                          <td style={{ ...tdStyle, fontWeight: abierta ? "700" : "400" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                              <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }} />
                              {row.red}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: "center", fontWeight: "600" }}>{row.nodos}</td>
                          <td style={{ ...tdStyle, textAlign: "center", padding: "6px" }}>
                            <span style={{ display: "inline-block", background: color, color: "#fff", fontWeight: "700", fontSize: "12px", padding: "3px 10px", borderRadius: "4px", minWidth: "48px", textAlign: "center" }}>{row.pct}%</span>
                          </td>
                        </tr>
                        {abierta && row.causas.map((c, j) => (
                          <tr key={`rc-${i}-${j}`} style={{ background: "#f0f4ff", borderLeft: `3px solid ${color}` }}>
                            <td style={{ ...tdStyle, borderBottom: `1px solid ${color}22` }}></td>
                            <td style={{ ...tdStyle, borderBottom: `1px solid ${color}22`, paddingLeft: "28px", fontSize: "12px", color: C.slate }}>
                              <span style={{ color: color, fontSize: "9px", marginRight: "6px" }}>●</span>{c.causa}
                            </td>
                            <td style={{ ...tdStyle, borderBottom: `1px solid ${color}22`, textAlign: "center", fontSize: "12px", color: C.slate, fontWeight: "600" }}>{c.nodos}</td>
                            <td style={{ ...tdStyle, borderBottom: `1px solid ${color}22`, textAlign: "center", padding: "5px 6px" }}>
                              <span style={{ display: "inline-block", background: "#f1f5f9", color: C.slate, fontWeight: "600", fontSize: "11px", padding: "2px 8px", borderRadius: "4px", minWidth: "40px", textAlign: "center" }}>{c.pct}%</span>
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  })
              }
              <tr style={{ background: C.navy }}>
                <td style={{ ...tdStyle, color: "white", fontWeight: "700", borderBottom: "none" }}></td>
                <td style={{ ...tdStyle, color: "white", fontWeight: "700", borderBottom: "none" }}>Total general</td>
                <td style={{ ...tdStyle, color: "white", fontWeight: "700", textAlign: "center", borderBottom: "none" }}>{total}</td>
                <td style={{ ...tdStyle, textAlign: "center", padding: "6px", borderBottom: "none" }}>
                  <span style={{ display: "inline-block", background: C.red, color: "white", fontWeight: "700", fontSize: "12px", padding: "3px 10px", borderRadius: "4px", minWidth: "48px", textAlign: "center" }}>100%</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Gráfica de barras horizontales */}
        <div style={{ ...cardStyle }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
            <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
            <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Distribución por Tipificación de Red</h3>
          </div>
          {tablaRed.length === 0
            ? <p style={{ color: C.slate, fontSize: "13px" }}>Sin datos</p>
            : <GraficaBarrasRed datos={tablaRed} />
          }
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "16px" }}>
            {tablaRed.map(d => (
              <span key={d.red} style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "11px", color: C.slate, fontWeight: "600" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: COLOR_RED[d.red] || "#64748b", display: "inline-block" }} />
                {d.red}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3. TABLAS ICR EXISTENTES (grilla 2 col, sin cambios) ─────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px", alignItems: "start" }}>

        {/* Causas Agrupadas ICR */}
        <div style={{ ...cardStyle }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
            <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Causas Agrupadas ICR</h3>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={{ ...thStyle, width: "22px", padding: "10px 4px" }}></th>
              <th style={thStyle}>Causas Agrupadas ICR</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Nodo</th>
              <th style={{ ...thStyle, textAlign: "center" }}>% Part</th>
            </tr></thead>
            <tbody>
              {tabla1.length === 0
                ? <tr><td colSpan={4} style={{ ...tdStyle, textAlign: "center", color: C.slate }}>Sin datos</td></tr>
                : tabla1.map((row, i) => {
                    const abierta = !!expandidas[row.agrupacion];
                    return (
                      <>
                        <tr key={`g-${i}`} onClick={() => toggleExpand(row.agrupacion)}
                          style={{ cursor: "pointer", background: abierta ? "#fff5f5" : (i % 2 === 0 ? C.white : "#f8fafc"), borderLeft: abierta ? `3px solid ${C.red}` : "3px solid transparent" }}>
                          <td style={{ ...tdStyle, textAlign: "center", padding: "8px 4px", fontSize: "10px", color: C.slate, userSelect: "none" }}>{abierta ? "▼" : "▶"}</td>
                          <td style={{ ...tdStyle, fontWeight: abierta ? "700" : "400" }}>{row.agrupacion}</td>
                          <td style={{ ...tdStyle, textAlign: "center", fontWeight: "600" }}>{row.nodos}</td>
                          <td style={{ ...tdStyle, textAlign: "center", padding: "6px" }}>{badge(row.pct)}</td>
                        </tr>
                        {abierta && row.causas.map((c, j) => (
                          <tr key={`c-${i}-${j}`} style={{ background: "#fef2f2", borderLeft: `3px solid ${C.red}` }}>
                            <td style={{ ...tdStyle, borderBottom: "1px solid #fecaca" }}></td>
                            <td style={{ ...tdStyle, borderBottom: "1px solid #fecaca", paddingLeft: "28px", fontSize: "12px", color: C.slate }}>
                              <span style={{ color: C.red, fontSize: "9px", marginRight: "6px" }}>●</span>{c.causa}
                            </td>
                            <td style={{ ...tdStyle, borderBottom: "1px solid #fecaca", textAlign: "center", fontSize: "12px", color: C.slate, fontWeight: "600" }}>{c.nodos}</td>
                            <td style={{ ...tdStyle, borderBottom: "1px solid #fecaca", textAlign: "center", padding: "5px 6px" }}>
                              <span style={{ display: "inline-block", background: "#f1f5f9", color: C.slate, fontWeight: "600", fontSize: "11px", padding: "2px 8px", borderRadius: "4px", minWidth: "40px", textAlign: "center" }}>{c.pct}%</span>
                            </td>
                          </tr>
                        ))}
                      </>
                    );
                  })
              }
              <tr style={{ background: C.navy }}>
                <td style={{ ...tdStyle, color: "white", fontWeight: "700", borderBottom: "none" }}></td>
                <td style={{ ...tdStyle, color: "white", fontWeight: "700", borderBottom: "none" }}>Total general</td>
                <td style={{ ...tdStyle, color: "white", fontWeight: "700", textAlign: "center", borderBottom: "none" }}>{total}</td>
                <td style={{ ...tdStyle, textAlign: "center", padding: "6px", borderBottom: "none" }}>
                  <span style={{ display: "inline-block", background: C.red, color: "white", fontWeight: "700", fontSize: "12px", padding: "3px 10px", borderRadius: "4px", minWidth: "48px", textAlign: "center" }}>100%</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Causas Incumplimiento ICR */}
        <div style={{ ...cardStyle }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
            <div style={{ width: "3px", height: "18px", background: C.red, borderRadius: "2px" }} />
            <h3 style={{ margin: 0, color: C.navy, fontSize: "14px", fontWeight: "600" }}>Causas Incumplimiento ICR</h3>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={thStyle}>Causas Incumplimiento ICR</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Nodo</th>
              <th style={{ ...thStyle, textAlign: "center" }}>% Part</th>
            </tr></thead>
            <tbody>
              {tabla2.length === 0
                ? <tr><td colSpan={3} style={{ ...tdStyle, textAlign: "center", color: C.slate }}>Sin datos</td></tr>
                : tabla2.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? C.white : "#f8fafc" }}>
                      <td style={tdStyle}>{row.causa}</td>
                      <td style={{ ...tdStyle, textAlign: "center", fontWeight: "600" }}>{row.nodos}</td>
                      <td style={{ ...tdStyle, textAlign: "center", padding: "6px" }}>{badge(row.pct)}</td>
                    </tr>
                  ))
              }
              <tr style={{ background: C.navy }}>
                <td style={{ ...tdStyle, color: "white", fontWeight: "700", borderBottom: "none" }}>Total general</td>
                <td style={{ ...tdStyle, color: "white", fontWeight: "700", textAlign: "center", borderBottom: "none" }}>{total}</td>
                <td style={{ ...tdStyle, textAlign: "center", padding: "6px", borderBottom: "none" }}>
                  <span style={{ display: "inline-block", background: C.red, color: "white", fontWeight: "700", fontSize: "12px", padding: "3px 10px", borderRadius: "4px", minWidth: "48px", textAlign: "center" }}>100%</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default CausasFalla;